import { CRONOFY_GET_EVENTS } from '@/gql/cronofy';
import { cronofyGetEventsPayload, cronofyGetEventsResponse } from '@/gql/cronofy/types';
import { CREATE_EVENT, EVENT_BY_PK, UPDATE_EVENT } from '@/gql/events';
import { CreateEventResponse, EventPayload, RecurrenceData, UpdateEventPayload } from '@/gql/events/types';
import { keys } from '@/gql/global/keys';
import { APIError } from '@/gql/global/types';
import { useCronofyProfiles, useCronofySyncEvent } from '@/services/cronofy/hooks';
import { fetchData } from '@/services/graphql';
import { queryClient } from '@/services/graphql/queryClient';
import { refetchActions } from '@/utils/action';
import { mergeServerCompletionsIntoRecurrence, normalizeEventPayloadForRecurrence } from '@/utils/events';
import { UseMutationOptions, useMutation, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

// TODO: Remove this in favour of queryClient stale time setting.
// Not setting now as certain pages do not handle stale data well yet.
const STALE_TIME = 10 * 60 * 60 * 1000;

/**
 * Optimistically write a pending recurrence into the cached actions and project-detail
 * queries (immutably, so structural sharing yields new identities and memoized views
 * re-render). A second star/status click before the refetch then reads the pending state
 * instead of a stale snapshot; the post-mutation refetch reconciles with the server.
 * In-flight fetches are cancelled first so a stale response can't overwrite the write.
 */
export function applyOptimisticEventRecurrence(eventId: string | undefined, recurrence: RecurrenceData) {
  if (!eventId) {
    return;
  }

  const patchActions = (actions: unknown) => {
    if (!Array.isArray(actions) || !actions.some((a: any) => a?.event?.id === eventId)) {
      return null;
    }
    return actions.map((a: any) => (a?.event?.id === eventId ? { ...a, event: { ...a.event, recurrence } } : a));
  };

  void queryClient.cancelQueries({ queryKey: keys.actions.all.queryKey });
  queryClient.setQueriesData({ queryKey: keys.actions.all.queryKey }, (old: any) => {
    const action = patchActions(old?.action);
    return action ? { ...old, action } : old;
  });

  void queryClient.cancelQueries({ queryKey: keys.project.detail._def });
  queryClient.setQueriesData({ queryKey: keys.project.detail._def }, (old: any) => {
    const actions = patchActions(old?.projectByPk?.actions);
    return actions ? { ...old, projectByPk: { ...old.projectByPk, actions } } : old;
  });
}

/** Roll an optimistic recurrence write back by refetching the canonical data. */
export function rollbackOptimisticEventRecurrence() {
  void queryClient.invalidateQueries({ queryKey: keys.actions.all.queryKey });
  void queryClient.invalidateQueries({ queryKey: keys.project.detail._def });
}

type UpdateEventMutateInput = UpdateEventPayload & {
  showSyncEvent?: boolean;
  /**
   * UPDATE_EVENT replaces the whole recurrence jsonb, so before writing we re-apply any
   * per-occurrence completions the server has that the payload lacks (payloads are built
   * from the client cache and usually drop exceptions.custom). Flows that intentionally
   * un-complete occurrences pass true to skip that preservation.
   */
  allowCompletionRemoval?: boolean;
};

export const useUpdateEventByPk = (options?: Omit<UseMutationOptions<any, APIError, UpdateEventMutateInput>, 'onSettled'>) => {
  const { mutate: cronofySyncEvent } = useCronofySyncEvent();

  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    mutationFn: async ({ showSyncEvent: _, allowCompletionRemoval, ...payload }: UpdateEventMutateInput) => {
      const normalized = normalizeEventPayloadForRecurrence({ ...payload });
      const recurrence = (normalized as { recurrence?: RecurrenceData | null }).recurrence;
      if (!allowCompletionRemoval && recurrence != null && Array.isArray(recurrence.rules)) {
        try {
          // noAuthRedirect: this pre-read is best-effort (we fall through to an unmerged
          // write); it must never bounce the user to sign-in if the role doesn't expose
          // eventByPk — but an expired token still refreshes so the merge keeps working.
          const data = await fetchData<{ eventByPk: { id: string; recurrence: unknown } | null }>(
            EVENT_BY_PK,
            { id: payload.id },
            { noAuthRedirect: true },
          );
          if (data?.eventByPk?.recurrence) {
            (normalized as { recurrence?: RecurrenceData }).recurrence = mergeServerCompletionsIntoRecurrence(
              recurrence,
              data.eventByPk.recurrence,
            );
          }
        } catch {
          // Pre-read failed: write the payload unmerged rather than block the user's action.
        }
      }
      return fetchData<any>(UPDATE_EVENT, normalized);
    },
    ...options,

    onSuccess: (data, variables, context) => {
      if (variables.showSyncEvent !== false) {
        cronofySyncEvent({ eventId: data.updateEventByPk.action.id, eventType: 'Action' });
      }

      options?.onSuccess?.(data, variables, context);

      refetchActions(data.updateEventByPk.action);
    },

    // Hook-level so it still fires when the component that called mutate() has unmounted
    // (mutate-level callbacks are dropped on unmount in react-query v4). Resyncs any
    // optimistic recurrence write with the server after a failure.
    onError: (error, variables, context) => {
      rollbackOptimisticEventRecurrence();
      options?.onError?.(error, variables, context);
    },
  });
};

export const useCreateEvent = (
  options?: Omit<UseMutationOptions<CreateEventResponse, APIError, EventPayload>, 'onSettled'>,
) => {
  const { mutate: cronofySyncEvent } = useCronofySyncEvent();

  return useMutation({
    mutationFn: (payload: EventPayload) => {
      return fetchData<CreateEventResponse>(CREATE_EVENT, normalizeEventPayloadForRecurrence(payload));
    },
    ...options,

    onSuccess: (data, variables, context) => {
      cronofySyncEvent({ eventId: data.insertEventOne.action.id, eventType: 'Action' });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useExternalEvents = (from: Date, to: Date) => {
  const { data: cronofyProfiles } = useCronofyProfiles();

  const enabledCalendarsIds = useMemo(
    () =>
      cronofyProfiles?.flatMap((profile) =>
        profile.calendars.filter((calendar) => calendar.inboundSync).map((calendar) => calendar.id),
      ) || [],
    [cronofyProfiles],
  );

  const queryState = useQuery({
    ...keys.cronofy.events(from, to, enabledCalendarsIds),
    queryFn: () => {
      // This is needed because once enabled turns true, ReactQuery immediately
      // fires the query that was previously disabled, causing an error because
      // enabledCalendarsIds is empty (and wasting a call)
      if (enabledCalendarsIds.length === 0) return Promise.resolve({ cronofyEvents: [] });
      return fetchData<cronofyGetEventsResponse>(CRONOFY_GET_EVENTS, {
        calendarsIds: enabledCalendarsIds,
        fromDt: from,
        toDt: to,
      } satisfies cronofyGetEventsPayload);
    },
    select: (data) => data?.cronofyEvents,
    enabled: enabledCalendarsIds.length > 0,
    staleTime: STALE_TIME,
  });

  return queryState;
};
